'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import LoginModal from '@/app/components/LoginModal';

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  preferred_language: string;
  instagram: string;
  introduction: string;
  consent: boolean;
}

const EMPTY_FORM: FormState = {
  full_name: '',
  email: '',
  phone: '',
  preferred_language: 'EN',
  instagram: '',
  introduction: '',
  consent: false,
};

export default function ApplyClient() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);

  function handleChange(field: keyof FormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Spam prevention: 30s cooldown
    const now = Date.now();
    if (now - lastSubmitTime < 30000) {
      setError(t('Please wait before submitting again.', '请稍后再提交。'));
      return;
    }

    if (!form.consent) {
      setError(t('Please agree to be contacted before submitting.', '请在提交前同意被联系。'));
      return;
    }

    setSubmitting(true);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('applications').insert({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        instagram: form.instagram.trim() || null,
        introduction: form.introduction.trim() || null,
        preferred_language: form.preferred_language || 'EN',
      });

      if (dbError) throw dbError;

      setLastSubmitTime(now);
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Submission failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header onLoginClick={() => setLoginOpen(true)} />
        <main className="pt-24 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              {t('Application Received', '申请已收到')}
            </h1>
            <p className="text-muted-foreground mb-3 leading-relaxed">
              {t(
                "Application received. We'll review it first. Once approved, you can use your account to join sessions.",
                '申请已收到。我们会先进行审核，通过后你可以使用账户加入训练。'
              )}
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              {t(
                'You can create your account now — it will be ready when your application is approved.',
                '你现在可以先创建账户，申请通过后即可使用。'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setLoginOpen(true)}
                className="btn-primary flex items-center gap-2 justify-center"
              >
                {t('Create / Log in to Account', '创建 / 登录账户')}
              </button>
              <Link href="/" className="btn-secondary flex items-center gap-2 justify-center">
                <ArrowLeft size={16} />
                {t('Back to Home', '返回首页')}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onLoginClick={() => setLoginOpen(true)} />
      <main className="pt-24 pb-20">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="mb-8">
            <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
              <ArrowLeft size={14} />
              {t('Back to Home', '返回首页')}
            </Link>
            <p className="text-card-label text-muted-foreground mb-2 tracking-widest text-xs font-semibold uppercase">
              R3SET RUNNING
            </p>
            <h1 className="text-3xl font-bold text-foreground mb-3">
              {t('Join R3SET Running', '加入 R3SET Running')}
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              {t(
                'Leave your contact and we\'ll review your application. More running details can be added later in your member profile.',
                '留下你的联系方式，我们会审核你的申请。更多跑步详情可在成为会员后在个人档案中补充。'
              )}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Required Fields */}
            <div className="bg-card rounded-xl border border-border p-5 sm:p-6 shadow-card space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  {t('Full Name', '姓名')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  placeholder={t('Your full name', '你的姓名')}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  {t('Email', '邮箱')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                />
              </div>

              {/* Phone / WhatsApp / WeChat */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  {t('Phone / WhatsApp / WeChat', '电话 / WhatsApp / 微信')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder={t('+852 XXXX XXXX or WeChat ID', '+852 XXXX XXXX 或微信号')}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                />
              </div>

              {/* Preferred Language */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  {t('Preferred Language', '偏好语言')} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  {(['EN', 'ZH'] as const).map((lang) => (
                    <label key={lang} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_language"
                        value={lang}
                        checked={form.preferred_language === lang}
                        onChange={() => handleChange('preferred_language', lang)}
                        className="w-4 h-4 accent-foreground"
                      />
                      <span className="text-sm text-foreground font-medium">
                        {lang === 'EN' ? 'EN' : '中文'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Optional Fields */}
            <div className="bg-card rounded-xl border border-border p-5 sm:p-6 shadow-card space-y-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('Optional', '选填')}
              </p>

              {/* Instagram */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Instagram
                </label>
                <input
                  type="text"
                  value={form.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder="@username"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all"
                />
              </div>

              {/* Short note */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  {t('Tell us briefly about yourself or your running background.', '简单介绍一下你自己或你的跑步背景。')}
                </label>
                <textarea
                  rows={3}
                  value={form.introduction}
                  onChange={(e) => handleChange('introduction', e.target.value)}
                  placeholder={t('A few sentences is fine!', '几句话就好！')}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/40 transition-all resize-none"
                />
              </div>
            </div>

            {/* Consent & Submit */}
            <div className="bg-card rounded-xl border border-border p-5 sm:p-6 shadow-card">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => handleChange('consent', e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-foreground rounded flex-shrink-0"
                />
                <span className="text-sm text-foreground leading-relaxed">
                  {t(
                    'I agree to be contacted by R3SET Running regarding my application and community updates.',
                    '我同意R3SET Running就我的申请及社群动态与我联系。'
                  )}
                  <span className="text-red-500 ml-1">*</span>
                </span>
              </label>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !form.consent}
                className="mt-5 w-full btn-primary flex items-center justify-center gap-2 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('Submitting...', '提交中...')}
                  </>
                ) : (
                  t('Submit Application', '提交申请')
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
